import { OverviewApi } from '../services'
import moment from 'moment';

const BENEFIT_TYPE_MAP = { ahc: 'health_checks' };

/**
 * Turns camelCase card tile keys (e.g. utilizationOfHealthChecks) into sentence-case UI labels.
 */
function formatCardTileLabel(key) {
  if (key == null || key === '') return key;
  const s = String(key);
  const spaced = s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  const lower = spaced.toLowerCase();
  const sentence = lower.charAt(0).toUpperCase() + lower.slice(1);
  return sentence.replace(/\bhra\b/g, 'HRA');
}


const SERVICE_CARD_DETAILS = {
  health_checks: {
    cardTiles: [
      'utilizationOfHealthChecks',
      'totalHealthChecksAvailed',
      'totalValueOfHealthChecksAvailed',
    ],
    cardLiners: () => [
      'Total sponsored health checks availed by your employees/ex-employees & their dependents',
      'Total value of sponsored health checks availed by employees/ex-employees & their dependents',
    ],
    btns: [{ text: 'View insights', link: '/test-results' }],
    viewDetailsLink: '/utilization/ahc',
    iconKey: 'health_checks',
    donutCardLiner: '*includes only sponsored active employees',
    requestApi: OverviewApi.fetchHealthChecksOverview,
  },
  consultations: {
    cardTiles: [
      'utilizationOfDoctorConsultation',
      'totalConsultationsTaken',
      'totalValueOfConsultationsAvailed',
    ],
    cardLiners: () => [
      'Total doctor consultations availed by your employees/ex-employees & their dependents',
      'Total value of doctor consultations availed by your employees/ex-employees & their dependents',
    ],
    viewDetailsLink: '/doctor-consultation',
    iconKey: 'consultations',
    donutCardLiner: '*includes sponsored active employees & their dependents',
    requestApi: OverviewApi.fetchConsultationsOverview,
  },
  dental: {
    cardTiles: ['utilizationOfDental', 'totalDentalChecksAvailed', 'totalValueOfDentalChecksAvailed'],
    cardLiners: () => [
      'Total sponsored dental checks availed by your employees/ex-employees & their dependents',
      'Total value of sponsored dental checks availed by your employees/ex-employees & their dependents',
    ],
    viewDetailsLink: '/utilization/dental',
    iconKey: 'dental',
    donutCardLiner: '*includes only sponsored active employees',
    requestApi: OverviewApi.fetchDentalOverview,
  },
  vision: {
    cardTiles: ['utilizationOfVision', 'totalVisionChecksAvailed', 'totalValueOfVisionChecksAvailed'],
    cardLiners: () => [
      'Total sponsored vision checks availed by your employees/ex-employees & their dependents',
      'Total value of sponsored vision checks availed by your employees/ex-employees & their dependents',
    ],
    viewDetailsLink: '/utilization/vision',
    iconKey: 'vision',
    donutCardLiner: '*includes only sponsored active employees',
    requestApi: OverviewApi.fetchVisionOverview,
  },
  hra: {
    cardTiles: ['employeesWhoCompletedHra', 'companyWellnessScore', 'companyWellnessGoalScore'],
    cardLiners: (isRunningContract) => [
      `Company wellness score for ${isRunningContract ? moment().format('MMMM') : 'the contract year'}`,
      'Company goal score',
    ],
    btns: [
      { text: 'View wellness score', link: '/wellness-score' },
      { text: 'View insights', link: '/health-risks' },
    ],
    iconKey: 'hra',
    donutCardLiner: '*includes only active employees',
    requestApi: OverviewApi.fetchHraOverview,
  },
  pre_employment: {
    cardTiles: ['totalOngoingAndCompletedChecks', 'readyToBookAppointments', 'awaitingApproval'],
    cardLiners: () => ['Ready to book appointments', 'Awaiting approval'],
    btns: [{ text: 'View insights', link: '/pre-employee-checkup' }],
    iconKey: 'pre_employment',
    requestApi: OverviewApi.fetchPreEmploymentOverview,
  },
};

const BENEFITS = ['ahc', 'consultations', 'dental', 'vision', 'pre_employment']

const NOT_ENABLED_BENEFITS = {
  ahc: { service_type: 'Health checks', iconKey: 'health_checks', tag: 'health checks' },
  dental: { service_type: 'Dental', iconKey: 'dental', tag: 'dental checks' },
  vision: { service_type: 'Vision', iconKey: 'vision', tag: 'vision checks' },
  pre_employment: { service_type: 'Pre employment', iconKey: 'pre_employment', tag: 'pre employment checks' },
  consultation24x7: { service_type: 'Doctor consultation', iconKey: 'consultations', tag: 'doctor consultations' },
}

const RELEASE_DATE = '10-05-2024'

let SALES_ROLE = null;

function getRoleFromSessionStorage() {
  return new Promise((resolve) => {
    const role = sessionStorage.getItem('role');
    if (role) {
      resolve(role);
    } else {
      window.addEventListener('roleSet', () => resolve(sessionStorage.getItem('role')));
    }
  });
}

async function main() {
  const role = await getRoleFromSessionStorage();
  SALES_ROLE = role.toLowerCase().replace(/\s/g, '');
}

main();



export { BENEFIT_TYPE_MAP, SERVICE_CARD_DETAILS, BENEFITS, NOT_ENABLED_BENEFITS, RELEASE_DATE, SALES_ROLE, formatCardTileLabel };

export const ALL_COUNTRIES_ISD_CODES = [
  { id: '+91', name: '+91 - India' },
  { id: '+60', name: '+60 - Malaysia' },
  { id: '+65', name: '+65 - Singapore' },
  { id: '+63', name: '+63 - Philippines'},
  { id: '+84', name: '+84 - Vietnam' },
  { id: '+976', name: '+976 - Mongolia' },
  { id: '+95', name: '+95 - Myanmar' }
]

export function formatAmount(number) {
  if (!number || isNaN(number)) return '0.0';

  const truncate = (num) => Math.trunc(num * 10) / 10;

  if (number < 1_000) return truncate(number).toFixed(1);
  if (number < 1_00_000) return truncate(number / 1_000).toFixed(1) + 'K';
  if (number < 1_00_00_000) return truncate(number / 1_00_000).toFixed(1) + 'L';
  return truncate(number / 1_00_00_000).toFixed(1) + 'Cr';
}

export const STATUS_DEFINITIONS = {
  'Settled': 'Claims or services that have been utilised and settled.',
  'Outstanding': 'Claims or requests that are under process — including those currently being verified, approved, or awaiting settlement or final payment.',
  'Rejected': 'Claims or services that have been reviewed but rejected during adjudication.',
  'Closed': 'Claims or services that were initiated but not fulfilled.'
};


